/* eslint-disable promise/catch-or-return */
import axios, { HttpStatusCode } from "axios";
import { useCallback, useTransition } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  apiPost,
  ApiSuffixType,
  apiUrlBase,
  applyGroupedServerSearchPaginationToBody,
  applyServerGridTextSearchToBody,
  applySimpleServerPaginationToBody,
  classifyServerSearchPayload,
  companyIdSelector,
  createErrorResult,
  createErrorResultFromException,
  createSuccessResult,
  CrudApi,
  defaultViewSF,
  entityIdSF,
  errorAndToastSimpleResult,
  errors,
  getLastFinalSchemaLocalStore,
  gridSchemaSF,
  GROUP_VALUES_PAGE_SIZE,
  GROUPING_GRID_NO_GROUPS,
  isEntityClientSideWithServerDatesFiltered,
  isEntityServerGroupedPagination,
  isEntityServerSideFiltered,
  isEntityServerSidePaginated,
  isGlobalSearchActiveSelector,
  isTranslatableItem,
  lastAppliedSearchCriteriaAF,
  Log,
  needToOptimizeSet,
  normalizeErrors,
  resolveFilterSchema,
  rowsFromServerSearchPayload,
  selectedTableRowsIdsAtomFamily,
  SINGLE_KEY,
  sumGroupValueCounts,
  tableColumnsSortingAF,
  tableFiltersSchemaAtomFamily,
  tableGridFetchingAtomFamily,
  tableGroupingAF,
  tableHelpers,
  tableRowsAtomFamily,
  tableRowsCountAtomFamily,
  tableRowsGroupValuesAtomFamily,
  tableRowsPaginationSf,
  tableSearchAF,
  tableServerGroupValuesAf,
  tableServerGroupValuesAllAf,
  tableServerGroupValuesPageAf,
  tableServerGroupValuesPagesAf,
  translateInfoItem,
  viewDtosAtomFamily,
  warnAndToastException,
  type ApiException,
  type IApiError,
  type IExtendableDto,
  type IGroupValue,
  type IGroupValuePaginationState,
  type IPaginatedGridSearchPayload,
  type IPaginationChangeEvent,
  type ISearchCriteria,
  type RydeUiUnit,
  type SearchQueryForOptimize,
  type SimpleResult,
} from "@ryde/shared";

import { useCustomQueryParams } from "../navigation";
import { useDynamicTablesCleanup, useStarredRows } from "./dynamic-tables";
import { useComposedSearchCriteria } from "./search";
import {
  fetchViews,
  getIsViewsFetchReady,
  resolveViewDtosToSet,
} from "./use-dynamic-forms.helpers";
import { useIsFetchAllowed } from "./use-is-fetch-allowed";
import { useSelectedIds } from "./use-selected-ids";

interface IFetchTableRowsArgs {
  isBypassClearSelection?: boolean;
  isPreloader?: boolean;
  skipPageReset?: boolean;
  isNotNeedToResetPagination?: boolean;
  isNeedToLoadMoreGroupValues?: boolean;
  groupPagination?: IPaginationChangeEvent;
  groupValuesPagination?: IGroupValuePaginationState[];
}

export const useDynamicFormsFetch = (
  entity: RydeUiUnit,
  searchQueryToOptimize?: SearchQueryForOptimize,
) => {
  const { t } = useTranslation();
  const setTableRows = useSetRecoilState(tableRowsAtomFamily(entity));
  const setTableRowsCount = useSetRecoilState(tableRowsCountAtomFamily(entity));
  const companyId = useRecoilValue(companyIdSelector);
  const setApiServerErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setIsFetching = useSetRecoilState(apiDataFetchingAtom);
  const setTableGridFetching = useSetRecoilState(
    tableGridFetchingAtomFamily(entity),
  );
  const setGridFetching = useEvent((isFetching: boolean) => {
    setIsFetching(isFetching);
    setTableGridFetching(isFetching);
  });
  const setViewDtos = useSetRecoilState(viewDtosAtomFamily(entity));
  const entityId = useRecoilValue(entityIdSF(entity));
  /**
   * `entityId` and `companyId` are resolved by independent login flows that are not awaited before a
   * grid page mounts, so either can arrive as `undefined` and resolve a moment later. Initialising a grid
   * before both land is not just early, it is wrong: there is no valid views request, and treating that
   * state as an empty list makes `isViewLoadedSF` report the views as loaded. The filters (and the rows
   * request built from them) then ignore the user's default view.
   *
   * Every mount init must therefore wait for both IDs rather than run twice. This flag gates
   * `initDynamicGridFetch` below and `fetchAndSetViews`, and `useInitViewsAndFilters` gates the page inits on
   * it — pages should go through that hook rather than repeat the check.
   */
  const isViewsFetchReady = getIsViewsFetchReady({ companyId, entityId });
  const defaultView = useRecoilValue(defaultViewSF(entity));
  const { defaultFilters } = useRecoilValue(gridSchemaSF(entity));
  const finalSchema = getLastFinalSchemaLocalStore(companyId ?? 0, entity);
  const [, startTransition] = useTransition();
  const { synchroStarredWithRows } = useStarredRows(entity);
  const isGlobalSearchActive = useRecoilValue(isGlobalSearchActiveSelector);
  const searchValue = useRecoilValue(tableSearchAF(entity));

  const { synchronizeSelectedRowsOnSearch, clearSelectedIds } =
    useSelectedIds();
  const setTableFilters = useSetRecoilState(
    tableFiltersSchemaAtomFamily(entity),
  );
  const { getIsFetchAllowed } = useIsFetchAllowed({ searchQueryToOptimize });
  const isNeedToBeOptimized = searchQueryToOptimize
    ? needToOptimizeSet.has(searchQueryToOptimize)
    : false;
  const { prepareServerSideTableFetch, prepareClientSideTableFetch } =
    useDynamicTablesCleanup({ entity });

  const { filtersSchemaFromQs, tableSearchFromQs } = useCustomQueryParams();
  const setTableSearch = useSetRecoilState(tableSearchAF(entity));

  const { getDynamicFormsComposedCriteria, getDynamicFormComposedPagination } =
    useComposedSearchCriteria(entity);

  const setLastAppliedSearchCriteria = useSetRecoilState(
    lastAppliedSearchCriteriaAF(entity),
  );

  const setSelectedIds = useSetRecoilState(
    selectedTableRowsIdsAtomFamily(entity),
  );
  const columnSorting = useRecoilValue(tableColumnsSortingAF(entity));
  const columnGrouping = useRecoilValue(tableGroupingAF(entity));
  const setTableGroupValuesRows = useSetRecoilState(
    tableRowsGroupValuesAtomFamily(entity),
  );
  const setTableServerGroupValues = useSetRecoilState(
    tableServerGroupValuesAf(entity),
  );
  const [tableServerGroupValuesAll, setTableServerGroupValuesAll] =
    useRecoilState(tableServerGroupValuesAllAf(entity));
  const [tableServerGroupValuesPage, setTableServerGroupValuesPage] =
    useRecoilState(tableServerGroupValuesPageAf(entity));
  const [tableServerGroupValuesPages, setTableServerGroupValuesPages] =
    useRecoilState(tableServerGroupValuesPagesAf(entity));
  const setTableRowsPagination = useSetRecoilState(
    tableRowsPaginationSf(entity),
  );

  const syncUngroupedServerPaginatedRows = useEvent(
    ({
      groupDataDtos,
      count,
    }: {
      groupDataDtos: IExtendableDto[];
      count: number;
    }): void => {
      setTableGroupValuesRows([
        {
          groupValue: GROUPING_GRID_NO_GROUPS,
          groupData: groupDataDtos,
        },
      ]);
      setTableServerGroupValues([
        { value: GROUPING_GRID_NO_GROUPS, count: String(count) },
      ]);
      setTableRowsCount(count);
    },
  );

  const initTableFilters = useEvent((): void => {
    const filtersSchemaComputed = resolveFilterSchema({
      isGlobalSearchActive,
      filtersSchemaFromQs,
      localStoredFinalSchemaFiltersSchema: finalSchema?.filtersSchema,
      viewFilterSchema: defaultView?.viewState?.filtersSchema,
      defaultFilters,
      entity,
    });
    setTableFilters(filtersSchemaComputed);

    if (tableSearchFromQs) {
      setTableSearch(tableSearchFromQs);
    }
  });

  const fetchAndSetViews = useEvent(async () => {
    const viewsResult = await fetchViews({ entityId, companyId });

    // A request that could not run because the IDs are still resolving is not a failure to report: the init
    // runs again once they land. `resolveViewDtosToSet` keeps the loading sentinel in place for that case.
    if (!viewsResult.success && isViewsFetchReady) {
      errorAndToastSimpleResult(viewsResult, t);
    }

    // Set on the failure path too. See `resolveViewDtosToSet`: leaving the atom `undefined` after a request
    // that failed keeps every loader waiting on `isViewLoadedSF` up for good.
    setViewDtos(previousViewDtos =>
      resolveViewDtosToSet({
        viewsResult,
        previousViewDtos,
        isViewsFetchReady,
      }),
    );

    return viewsResult;
  });

  const fetchTableServerGroupValues = useEvent(
    async (args?: {
      isPreloader?: boolean;
    }): Promise<SimpleResult<IGroupValuePaginationState[]>> => {
      const isPreloader = args?.isPreloader ?? true;
      const groupByField = tableHelpers.getGroupByField(columnGrouping);

      if (!groupByField || !companyId) {
        return createErrorResult(errors.common.technicalProblemOccurred);
      }

      const requestUrl = apiUrlBase(
        entity,
        ApiSuffixType.NoSuffix,
        "groupValues",
      );
      const composedQueryObject = getDynamicFormsComposedCriteria();
      const body: ISearchCriteria<IExtendableDto> = {
        where: composedQueryObject,
        groupByField,
        orderByField: tableHelpers.getOrderByField(columnSorting),
        orderByDirection: tableHelpers.getOrderByDirection(columnSorting),
      };

      applyServerGridTextSearchToBody({ body, entity, searchValue });

      if (isPreloader) {
        setGridFetching(true);
      }

      try {
        const groupValuesResult = await apiPost<SimpleResult<IGroupValue[]>>(
          requestUrl,
          { body },
        );

        if (!groupValuesResult?.success || !groupValuesResult.data) {
          setApiServerErrors([t(errors.common.technicalProblemOccurred)]);
          return createErrorResult(errors.common.technicalProblemOccurred);
        }

        const groupValues = groupValuesResult.data;
        const groupValuesPages = Math.ceil(
          groupValues.length / GROUP_VALUES_PAGE_SIZE,
        );
        const groupValuesPage = 1;
        const groupValuesActive = groupValues.slice(0, GROUP_VALUES_PAGE_SIZE);
        const groupValuePagination: IGroupValuePaginationState[] =
          groupValuesActive.map(el => ({
            value: el.value,
            ...tableHelpers.getDefaultPagination(columnGrouping),
          }));

        setTableServerGroupValuesAll(groupValues);
        setTableServerGroupValuesPages(groupValuesPages);
        setTableServerGroupValuesPage(groupValuesPage);
        setTableServerGroupValues(groupValuesActive);
        setTableRowsPagination(groupValuePagination);
        // A grouped search returns no count, so the grid's total comes from its groups.
        setTableRowsCount(sumGroupValueCounts(groupValues));

        return createSuccessResult(groupValuePagination);
      } catch (error) {
        Log.errorAndToast(t("errors.failedToFetchTableRows"), error);
        setApiServerErrors(normalizeErrors(error as IApiError, t));

        return createErrorResultFromException(error);
      } finally {
        if (isPreloader) {
          setGridFetching(false);
        }
      }
    },
  );

  const fetchTableServerGroupValuesMore = useEvent(
    async (): Promise<IGroupValuePaginationState[]> => {
      const page = tableServerGroupValuesPage + 1;
      const start = (page - 1) * GROUP_VALUES_PAGE_SIZE;
      const end = start + GROUP_VALUES_PAGE_SIZE;
      const groupValuesActive = tableServerGroupValuesAll.slice(start, end);
      const groupValuePagination: IGroupValuePaginationState[] =
        groupValuesActive.map(el => ({
          value: el.value,
          ...tableHelpers.getDefaultPagination(columnGrouping),
        }));

      setTableServerGroupValues(oldValues => [
        ...oldValues,
        ...groupValuesActive,
      ]);
      setTableRowsPagination(oldValues => [
        ...oldValues,
        ...groupValuePagination,
      ]);
      setTableServerGroupValuesPage(page);

      return groupValuePagination;
    },
  );

  const fetchTableServerGroupValuesMoreIsAvailable = useEvent((): boolean => {
    return tableServerGroupValuesPage < tableServerGroupValuesPages;
  });

  const fetchTableRowsServerFiltered = useEvent(
    async ({
      isBypassClearSelection,
      skipPageReset,
      isPreloader = true,
      groupPagination,
      groupValuesPagination,
      isNeedToLoadMoreGroupValues,
      isNotNeedToResetPagination,
    }: IFetchTableRowsArgs = {}): Promise<boolean> => {
      const cancelTokenSource = axios.CancelToken.source();
      const isServerPagination = isEntityServerSidePaginated(entity);

      try {
        if (!companyId) {
          Log.errorAndToast(t("errors.invalidCompanyIdForEntity", { entity }));
          return false;
        }

        prepareServerSideTableFetch({
          skipPageReset: skipPageReset ?? isNotNeedToResetPagination,
        });

        if (isPreloader) {
          setGridFetching(true);
        }

        const composedQueryObject = getDynamicFormsComposedCriteria();
        Log.debug(
          `Server fetch criteria object for ${entity}`,
          composedQueryObject,
        );
        const body: ISearchCriteria<IExtendableDto> = {
          where: composedQueryObject,
        };

        if (isServerPagination) {
          if (isEntityServerGroupedPagination(entity)) {
            applyGroupedServerSearchPaginationToBody({
              body,
              groupByField: tableHelpers.getGroupByField(columnGrouping),
              paginationResult: getDynamicFormComposedPagination(
                groupPagination,
                groupValuesPagination,
              ),
              orderByField: tableHelpers.getOrderByField(columnSorting),
              orderByDirection: tableHelpers.getOrderByDirection(columnSorting),
              groupPagination,
              resetToFirstPage:
                !(skipPageReset ?? isNotNeedToResetPagination) &&
                !groupPagination,
              pageSize: tableHelpers.getPageSize(columnGrouping),
            });
          } else {
            applySimpleServerPaginationToBody(
              body,
              getDynamicFormComposedPagination(),
            );
          }

          applyServerGridTextSearchToBody({ body, entity, searchValue });
        }

        if (isNeedToBeOptimized) {
          const isFetchAllowed = await getIsFetchAllowed(body);

          if (!isFetchAllowed) {
            return false;
          }
        }

        const serverSearchResult = await CrudApi.post<
          SimpleResult<IPaginatedGridSearchPayload<IExtendableDto>>
        >({
          entity,
          data: body,
          suffixType: ApiSuffixType.ServerSearch,
          cancelToken: cancelTokenSource.token,
        });

        if (
          !serverSearchResult ||
          (serverSearchResult.success && !serverSearchResult.data)
        ) {
          setApiServerErrors([t(errors.common.emptyApiResponse)]);
          return false;
        }

        if (!serverSearchResult.success) {
          const error = serverSearchResult.error;

          if (isTranslatableItem(error)) {
            setApiServerErrors([translateInfoItem(error, t)]);
          } else {
            setApiServerErrors([t(errors.ride.failedFetchingRides)]);
          }

          return false;
        }

        const searchData = serverSearchResult.data;

        Log.debug(`Server data received  for ${entity}`);

        const parsed = classifyServerSearchPayload(searchData);

        if (!parsed) {
          setApiServerErrors([t(errors.common.emptyApiResponse)]);
          return false;
        }

        setLastAppliedSearchCriteria(body.where);

        let dtos: IExtendableDto[] = [];

        if (parsed.kind === "groups") {
          if (groupPagination) {
            setTableGroupValuesRows(oldData =>
              oldData.map(el => {
                if (el.groupValue === groupPagination.groupingValue) {
                  return {
                    ...el,
                    groupData: parsed.groups[0].groupData,
                  };
                }

                return el;
              }),
            );
          } else if (isNeedToLoadMoreGroupValues && groupValuesPagination) {
            setTableGroupValuesRows(oldValues => [
              ...oldValues,
              ...parsed.groups,
            ]);
          } else {
            setTableGroupValuesRows(parsed.groups);
          }
        } else if (parsed.kind === "paginated") {
          dtos = parsed.items;

          if (isEntityServerGroupedPagination(entity)) {
            syncUngroupedServerPaginatedRows({
              groupDataDtos: dtos,
              count: parsed.count,
            });
          } else {
            setTableRowsCount(parsed.count);
          }
        } else {
          dtos = parsed.items;
        }

        if (!isBypassClearSelection) {
          synchronizeSelectedRowsOnSearch(
            rowsFromServerSearchPayload(searchData),
            composedQueryObject,
          );
        }

        setTableRows(dtos);
        return true;
      } catch (error) {
        if (axios.isCancel(error)) {
          Log.info("Axios, request canceled", error);
          return false;
        }
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          warnAndToastException({
            error,
            fallbackTranslationKey: errors.common.dontHavePermissionForAction,
            translationFunction: t,
          });
          return false;
        }

        Log.errorAndToast(t("errors.failedToFetchTableRows"), error);
        setApiServerErrors(normalizeErrors(error as IApiError, t));

        return false;
      } finally {
        if (isPreloader) {
          setGridFetching(false);
        }
      }
    },
  );

  const fetchTableRowsClientFiltered = useEvent(
    async (args?: { isPreloader?: boolean }): Promise<boolean> => {
      const isPreloader = args?.isPreloader ?? true;

      try {
        Log.debug(`Fetch table rows with CLIENT-SIDE filters  for ${entity}`);

        if (!companyId) {
          Log.errorAndToast(t("errors.invalidCompanyId"));
          return false;
        }

        prepareClientSideTableFetch();

        if (isPreloader) {
          setGridFetching(true);
        }

        const rowsFromApi = await CrudApi.getAll<IExtendableDto>(
          entity,
          ApiSuffixType.NoSuffix,
        );

        if (!rowsFromApi?.length) {
          clearSelectedIds();
        } else {
          setSelectedIds(selIds =>
            selIds.filter(selId => rowsFromApi.some(row => row.id === selId)),
          );
        }

        synchroStarredWithRows(rowsFromApi);

        setTableRows(rowsFromApi || []);

        return true;
      } catch (error) {
        if (axios.isCancel(error)) {
          Log.debug(`Axios, request canceled for ${entity}`, error);
          return false;
        }
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          warnAndToastException({
            error,
            fallbackTranslationKey: errors.common.dontHavePermissionForAction,
            translationFunction: t,
          });
          return false;
        }

        Log.errorAndToast(
          t("errors.failedToFetchTableRowsForEntity", {
            entity: t(`${entity}.${SINGLE_KEY}`),
          }),
          error,
        );
        setApiServerErrors(normalizeErrors(error as IApiError, t));
        return false;
      } finally {
        if (isPreloader) {
          setGridFetching(false);
        }
      }
    },
  );
  const fetchTableRowsAndGroupValues = useEvent(
    async (args?: IFetchTableRowsArgs): Promise<void> => {
      if (!isEntityServerGroupedPagination(entity)) {
        await fetchTableRowsServerFiltered(args);
        return;
      }

      setGridFetching(true);
      const groupByField = tableHelpers.getGroupByField(columnGrouping);
      let groupValuesPagination: IGroupValuePaginationState[] = [];

      if (groupByField) {
        if (args?.groupPagination) {
          await fetchTableRowsServerFiltered({
            ...args,
            isPreloader: false,
          });
          setGridFetching(false);
          return;
        }

        if (!args?.isNeedToLoadMoreGroupValues) {
          const groupValuesResult = await fetchTableServerGroupValues({
            isPreloader: false,
          });

          // Rows requested without groups come back ungrouped and replace the grid's groups,
          // so stop here and keep them along with the error.
          if (!groupValuesResult.success) {
            setGridFetching(false);
            return;
          }

          groupValuesPagination = groupValuesResult.data ?? [];
        } else {
          groupValuesPagination = await fetchTableServerGroupValuesMore();
        }

        await fetchTableRowsServerFiltered({
          ...args,
          isPreloader: false,
          groupValuesPagination,
        });
      } else {
        await fetchTableRowsServerFiltered({ ...args, isPreloader: false });
      }

      setGridFetching(false);
    },
  );

  const fetchTableRows = useCallback(
    (args?: { isPreloader?: boolean }) => {
      startTransition(() => {
        if (
          isEntityServerSideFiltered(entity) ||
          isEntityClientSideWithServerDatesFiltered(entity)
        ) {
          if (isEntityServerGroupedPagination(entity)) {
            fetchTableRowsAndGroupValues(args).finally(() =>
              setGridFetching(false),
            );
          } else {
            fetchTableRowsServerFiltered(args).finally(() =>
              setGridFetching(false),
            );
          }
        } else {
          fetchTableRowsClientFiltered(args).finally(() =>
            setGridFetching(false),
          );
        }
      });
    },
    [
      entity,
      fetchTableRowsAndGroupValues,
      fetchTableRowsClientFiltered,
      fetchTableRowsServerFiltered,
      setGridFetching,
    ],
  );

  const initDynamicGridFetch = useCallback(async (): Promise<void> => {
    if (!isViewsFetchReady) {
      return;
    }

    setGridFetching(true);
    // A failed views request is reported and settled inside `fetchAndSetViews`, so the grid still initializes
    // from the stored/default schema. Returning early instead would leave the page loading with no rows.
    await fetchAndSetViews();

    initTableFilters();
    if (isEntityServerGroupedPagination(entity)) {
      await fetchTableRowsAndGroupValues({ isPreloader: false });
    } else {
      fetchTableRows({ isPreloader: false });
    }
  }, [
    entity,
    isViewsFetchReady,
    setGridFetching,
    fetchAndSetViews,
    initTableFilters,
    fetchTableRows,
    fetchTableRowsAndGroupValues,
  ]);

  return {
    fetchTableServerGroupValues,
    fetchTableServerGroupValuesMore,
    fetchTableServerGroupValuesMoreIsAvailable,
    fetchTableRowsServerFiltered,
    fetchTableRowsClientFiltered,
    fetchTableRows,
    fetchTableRowsAndGroupValues,
    fetchAndSetViews,
    initTableFilters,
    initDynamicGridFetch,
    isViewsFetchReady,
  };
};
